import {
  Component, inject, signal, viewChild,
  ElementRef, effect, untracked, DestroyRef,
  afterNextRender
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageBarService, PageBarTab } from 'src/app/services/page-bar.service';
import { DeviceService } from 'src/app/services/device.service';
import { NavLeftService } from 'src/app/services/nav-left.service';

@Component({
  selector:    'app-page-bar',
  standalone:  true,
  imports:     [CommonModule],
  templateUrl: './page-bar.component.html',
  styleUrls:   ['./page-bar.component.scss'],
})
export class PageBarComponent {
  protected bar    = inject(PageBarService);
  protected device = inject(DeviceService);
  private destroyRef = inject(DestroyRef);
  private   navLeft = inject(NavLeftService);

  // ── Refs DOM — required() car toujours présents dans le template ──────────
  readonly tabsWrapper     = viewChild.required<ElementRef<HTMLElement>>('tabsWrapper');
  readonly scrollableInner = viewChild.required<ElementRef<HTMLElement>>('scrollableInner');

  // ── État scroll ───────────────────────────────────────────────────────────
  protected canScrollLeft  = signal(false);
  protected canScrollRight = signal(false);

  constructor() {
    // Initialisation après le premier rendu DOM
    afterNextRender(() => {
      this.setupScrollListener();
      this.setupResizeObserver();
      this.checkScrollButtons();
    });

    // Réagit aux changements de device sans setTimeout
    effect(() => {
      this.device.device(); // souscription signal
      // untracked pour ne pas créer de dépendance circulaire
      untracked(() => this.checkScrollButtons());
    });
  }

  // ── Setup ─────────────────────────────────────────────────────────────────

  private setupScrollListener(): void {
    const el = this.scrollableInner().nativeElement;
    const handler = () => this.checkScrollButtons();

    el.addEventListener('scroll', handler, { passive: true });

    // Nettoyage automatique via DestroyRef — plus besoin de ngOnDestroy
    this.destroyRef.onDestroy(() => el.removeEventListener('scroll', handler));
  }

  private setupResizeObserver(): void {
    const observer = new ResizeObserver(() => this.checkScrollButtons());
    observer.observe(this.tabsWrapper().nativeElement);

    // Nettoyage automatique
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  // ── Logique scroll ────────────────────────────────────────────────────────

  checkScrollButtons(): void {
    const el = this.scrollableInner().nativeElement;
    this.canScrollLeft.set(el.scrollLeft > 0);
    this.canScrollRight.set(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const el = this.scrollableInner().nativeElement;
    el.scrollLeft += direction === 'left' ? -200 : 200;
    // scrollend event — plus fiable que setTimeout
    el.addEventListener('scrollend', () => this.checkScrollButtons(), { once: true });
  }

  // ── Callbacks ─────────────────────────────────────────────────────────────

  onTab(tab: PageBarTab): void {
    this.bar.setActiveTab(tab.id);
    this.bar.config().onTabClick?.(tab);
  }

  onBack(): void {
    this.bar.config().onBack?.();
    this.navLeft.showMobileNav();
  }
}